import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Users, 
  FileText, 
  IndianRupee, 
  TrendingUp,
  AlertCircle,
  RefreshCw,
  Calendar,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClient } from "@/context/ClientContext";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { selectedClient, clientId } = useClient();
  const [stats, setStats] = useState(null);
  const [periodData, setPeriodData] = useState(null);
  const [periods, setPeriods] = useState({ financial_years: [], quarters: [], months: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedFY, setSelectedFY] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");

  const fetchPeriods = useCallback(async () => {
    try {
      const params = clientId ? { client_id: clientId } : {};
      const response = await axios.get(`${API}/periods`, { params });
      setPeriods(response.data);
      if (response.data.financial_years.length > 0) {
        setSelectedFY(prev => prev || response.data.financial_years[0]);
      }
    } catch (err) {
      /* periods are supplementary - UI remains functional without them */
      devWarn("fetchPeriods (non-critical):", err);
    }
  }, [clientId]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (clientId) params.client_id = clientId;
      if (selectedFY && selectedFY !== "all") params.financial_year = selectedFY;
      if (selectedQuarter && selectedQuarter !== "all") params.quarter = selectedQuarter;
      
      const [statsRes, periodRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { params }),
        axios.get(`${API}/summary/period-wise`, { params: { ...params, financial_year: selectedFY } })
      ]);
      setStats(statsRes.data);
      setPeriodData(periodRes.data);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Unknown error";
      setError(`Failed to load dashboard data: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedFY, selectedQuarter]);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  if (!selectedClient) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500" data-testid="no-client">
        <Building2 className="w-16 h-16 mb-4 text-slate-300" />
        <p className="text-lg font-medium">No Client Selected</p>
        <p className="text-sm mt-1">Please select a client from the sidebar to view dashboard</p>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500" data-testid="dashboard-error">
        <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
        <p className="mb-4">{error}</p>
        <Button onClick={fetchStats} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {selectedClient.name} - TDS Overview
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="w-36" data-testid="fy-filter">
                <SelectValue placeholder="All FY" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {periods.financial_years.map((fy) => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
            <SelectTrigger className="w-28" data-testid="quarter-filter">
              <SelectValue placeholder="All Qtrs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Qtrs</SelectItem>
              <SelectItem value="Q1">Q1 (Apr-Jun)</SelectItem>
              <SelectItem value="Q2">Q2 (Jul-Sep)</SelectItem>
              <SelectItem value="Q3">Q3 (Oct-Dec)</SelectItem>
              <SelectItem value="Q4">Q4 (Jan-Mar)</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchStats}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <Card className="stat-card" data-testid="stat-ledgers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Party Ledgers</p>
                <p className="stat-value">{formatNumber(stats?.total_ledgers || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-transactions">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">TDS Transactions</p>
                <p className="stat-value">{formatNumber(stats?.total_transactions || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-amount">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Amount</p>
                <p className="stat-value text-xl">{formatCurrency(stats?.total_amount || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-tds">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total TDS</p>
                <p className="stat-value text-xl">{formatCurrency(stats?.total_tds || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Period-wise Summary */}
      {periodData && (
        <div className="grid grid-cols-2 gap-6 mb-6">
          <Card className="data-card" data-testid="quarterly-summary">
            <CardHeader className="data-card-header">
              <CardTitle className="data-card-title">Quarterly Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {periodData.quarterly?.length > 0 ? (
                <table className="tds-table">
                  <thead>
                    <tr>
                      <th>Quarter</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">TDS</th>
                      <th className="text-right">Txns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodData.quarterly.map((q) => (
                      <tr key={`${q.quarter}-${q.total_amount}`}>
                        <td><span className="section-badge">{q.quarter}</span></td>
                        <td className="amount-cell">{formatCurrency(q.total_amount)}</td>
                        <td className="amount-cell">{formatCurrency(q.total_tds)}</td>
                        <td className="amount-cell">{q.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state py-8">
                  <p className="text-slate-400 text-sm">No quarterly data</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="data-card" data-testid="monthly-summary">
            <CardHeader className="data-card-header">
              <CardTitle className="data-card-title">Monthly Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-64 overflow-auto">
              {periodData.monthly?.length > 0 ? (
                <table className="tds-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">TDS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodData.monthly.map((m) => (
                      <tr key={`${m.month}-${m.total_amount}`}>
                        <td>{m.month}</td>
                        <td className="amount-cell">{formatCurrency(m.total_amount)}</td>
                        <td className="amount-cell">{formatCurrency(m.total_tds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-state py-8">
                  <p className="text-slate-400 text-sm">No monthly data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section Breakdown */}
      <Card className="data-card" data-testid="section-breakdown">
        <CardHeader className="data-card-header">
          <CardTitle className="data-card-title">TDS Section-wise Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {stats?.section_breakdown && stats.section_breakdown.length > 0 ? (
            <table className="tds-table">
              <thead>
                <tr>
                  <th>Section</th>
                  <th className="text-right">Transactions</th>
                  <th className="text-right">Total Amount</th>
                  <th className="text-right">Total TDS</th>
                  <th className="text-right">Effective Rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.section_breakdown.map((section, idx) => {
                  const effectiveRate = section.total_amount > 0 
                    ? (section.total_tds / section.total_amount * 100).toFixed(2)
                    : "0.00";
                  return (
                    <tr key={section.section} data-testid={`section-row-${section.section}`}>
                      <td><span className="section-badge">{section.section}</span></td>
                      <td className="amount-cell">{section.count}</td>
                      <td className="amount-cell">{formatCurrency(section.total_amount)}</td>
                      <td className="amount-cell">{formatCurrency(section.total_tds)}</td>
                      <td className="amount-cell">{effectiveRate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <FileText className="empty-state-icon" />
              <p className="empty-state-title">No data available</p>
              <p className="empty-state-description">
                Upload party ledgers and TDS transactions to see the breakdown
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
